import { observable } from "mobx";
import {
  POINT_SCALES,
  PointsEventData,
  PointsEventType,
} from "@citychallenge/common";
import { useScene } from "./use-scene";
import { TransTokens } from "../../i18n";

export const PointsTokens = TransTokens.create({
  complete: "Completed",
  bonus: "Timebonus",
  hints: "{{num}} hint",
  hintsMultiple: "{{num}} hints",
  wrong: "{{num}} mistake",
  wrongMultiple: "{{num}} mistakes",
  total: "+{{num}} points",
});

// Lives in @citychallenge/common so the builder's points selector, this scoring
// code and the server's session-score verification share one definition. Kept
// re-exported here because the player imports it from this module.
export { POINT_SCALES };

export class PointsEvent implements PointsEventData {
  @observable
  public points: number;

  @observable
  public type: PointsEventType;

  @observable
  public label?: string;

  @observable
  public created?: number;

  constructor(data: PointsEventData) {
    this.points = data.points;
    this.label = data.label;
    this.created = data.created || +new Date();
    this.type = data.type;
  }

  public toJSON() {
    return {
      points: this.points,
      label: this.label,
      type: this.type,
    };
  }
}

export function calculatePointsChange(
  amount: number,
  scaleKey: keyof typeof POINT_SCALES
) {
  const scales = POINT_SCALES[scaleKey];

  if (amount === 0) return 0;
  return scales.reduce((current, val, index) => {
    if (index < amount) {
      return current + val;
    }

    return current;
  }, 0);
}

export function useScenePoints() {
  const {
    hintsUsedPenalty,
    wrongAnswersPenalty,
    wrongAnswers,
    hintsUsed,
    timeBonusPoints,
    maxPoints,
  } = useScene();

  const bonusScale =
    timeBonusPoints && !wrongAnswers && !hintsUsed
      ? POINT_SCALES.timeBonus[0]
      : 0;

  const wrongScale = wrongAnswersPenalty / maxPoints;
  const hintsScale = hintsUsedPenalty / maxPoints;
  const completeScale = 1 - bonusScale;

  return { bonusScale, wrongScale, hintsScale, completeScale };
}
